import React, { useEffect } from "react";
import { connect } from "react-redux";
import { ClockSvg, ShopSvg, VendorMailSvg } from "../../../components/CommonSvg";
import { getTicketCommentsRequest } from "../../../redux/admin/supportTickets/action";

/* a ticket, not the headset the support icon draws - the header is about this
   one ticket, so the icon should say ticket */
const TicketSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3 8.5V6.5C3 5.67 3.67 5 4.5 5H19.5C20.33 5 21 5.67 21 6.5V8.5C19.62 8.5 18.5 9.62 18.5 11C18.5 12.38 19.62 13.5 21 13.5V15.5C21 16.33 20.33 17 19.5 17H4.5C3.67 17 3 16.33 3 15.5V13.5C4.38 13.5 5.5 12.38 5.5 11C5.5 9.62 4.38 8.5 3 8.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M13.5 8.5V13.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeDasharray="2 2.5"
    />
  </svg>
);

/* the owner sits on its own line with an icon, the same way the store does */
const PersonSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M5 19.5C5 16.1 8.13 14 12 14C15.87 14 19 16.1 19 19.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

/* the status decides the colour of the rail, the pill and the card's edge, so
   it is worked out once and reused as a class suffix */
const statusSuffix = (status) => {
  const value = String(status || "").toLowerCase();
  if (value.includes("follow") || value.includes("pend")) return "follow";
  if (value.includes("open") || value.includes("new")) return "open";
  if (value.includes("progress") || value.includes("hold") || value.includes("wait"))
    return "prog";
  if (value.includes("clos") || value.includes("resolv") || value.includes("complet"))
    return "done";
  return "neu";
};

const prioritySuffix = (priority) => {
  const value = String(priority || "").toLowerCase();
  if (value.includes("high") || value.includes("urgent")) return "high";
  if (value.includes("low")) return "low";
  return "med";
};

const initials = (name, email) => {
  const source = String(name || email || "").trim();
  if (!source) return "—";
  return (
    source
      .split(/[\s._-]+/)
      .filter(Boolean)
      .map((word) => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "—"
  );
};

/* the forward lists are stored as one comma separated string, the way the
   status change modal sent them - the card shows them one address at a time */
const emailList = (value) =>
  String(value || "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);

function TicketCommentList(props) {
  const { ticket, comments, loading, getTicketCommentsRequest } = props;

  /* the ticket's own row id is what the comments hang off - ticketId on the row
     is the Zoho one, and support-comments does not hold that */
  const ticketRowId = ticket?.ID;

  useEffect(() => {
    if (ticketRowId) {
      getTicketCommentsRequest({ ticketId: ticketRowId });
    }
  }, [ticketRowId]);

  /* the API already answers newest first, which is the order the screen wants */
  const ordered = comments || [];

  const owner = `${ticket?.member?.firstName || ""} ${
    ticket?.member?.lastName || ""
  }`.trim();

  return (
    <div className="ticket-comments">
      <div className="tc-head">
        <div className="tc-head-id-wrap">
          <span className="tc-head-icon">
            <TicketSvg />
          </span>
          <div className="tc-head-id">
            <b>{ticket?.ticketName || "-"}</b>
            <i>
              <PersonSvg />
              <span title={owner || ticket?.ownerEmail || ""}>
                {owner || ticket?.ownerEmail || "-"}
              </span>
            </i>
            <i>
              <ShopSvg />
              <span
                title={ticket?.store || ticket?.member?.storeNameRegular || ""}
              >
                {ticket?.store || ticket?.member?.storeNameRegular || "-"}
              </span>
            </i>
          </div>
        </div>

        <div className="tc-head-meta">
          <label>Current Status</label>
          <span className={`tc-pill st-${statusSuffix(ticket?.status)}`}>
            <i className="tc-pill-dot" />
            {ticket?.status || "-"}
          </span>
        </div>
        <div className="tc-head-meta">
          <label>Priority</label>
          <span className={`tc-meta-value pr-${prioritySuffix(ticket?.priority)}`}>
            <i className="tc-dot" />
            {ticket?.priority || "-"}
          </span>
        </div>
      </div>

      <div className="tc-bar">
        <b>All Comments ({ordered.length})</b>
      </div>

      {loading ? (
        <p className="tc-empty">Loading comments…</p>
      ) : ordered.length === 0 ? (
        <p className="tc-empty">No comments have been added yet.</p>
      ) : (
        <ul className="tc-list">
          {ordered.map((row) => {
            const suffix = statusSuffix(row.status);
            const cc = emailList(row.addCC);
            const bcc = emailList(row.addBCC);
            return (
              <li className={`tc-item st-${suffix}`} key={row.id}>
                <div className="tc-card">
                  <div className="tc-card-top">
                    <span className="tc-avatar">
                      {initials(row.vendorUserName, row.vendorUserEmail)}
                    </span>
                    <div className="tc-who">
                      <b>
                        {row.vendorUserName || row.vendorUserEmail || "Unknown"}
                        <em>Added by</em>
                      </b>
                      {row.vendorUserEmail ? <span>{row.vendorUserEmail}</span> : null}
                    </div>
                    {/* the status the comment was filed with - it used to sit on
                        a rail down the left, which was more chrome than it was
                        worth */}
                    <span className={`tc-pill st-${suffix}`}>{row.status || "-"}</span>
                    <span className="tc-when">
                      <ClockSvg />
                      {row.createdDate || "-"}
                    </span>
                  </div>

                  {/* the vendor typed it as they meant it, so the breaks stay */}
                  <p className="tc-body">{row.comment || "-"}</p>

                  <div className="tc-card-foot">
                    <span className="tc-vendor">
                      <ShopSvg />
                      <label>
                        Vendor
                        <b>{row.vendorName || "-"}</b>
                      </label>
                    </span>

                    {/* who else the status change went out to - only worth the
                        room when the vendor actually copied someone */}
                    {cc.length > 0 || bcc.length > 0 ? (
                      <span className="tc-copies">
                        <VendorMailSvg />
                        <span className="tc-copy-groups">
                          {cc.length > 0 ? (
                            <label>
                              CC
                              <span className="tc-copy-emails">
                                {cc.map((email) => (
                                  <b key={`cc-${email}`} title={email}>
                                    {email}
                                  </b>
                                ))}
                              </span>
                            </label>
                          ) : null}
                          {bcc.length > 0 ? (
                            <label>
                              BCC
                              <span className="tc-copy-emails">
                                {bcc.map((email) => (
                                  <b key={`bcc-${email}`} title={email}>
                                    {email}
                                  </b>
                                ))}
                              </span>
                            </label>
                          ) : null}
                        </span>
                      </span>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const mapStateToProps = ({ SupportTicket }) => {
  return {
    comments: SupportTicket?.ticketComments?.list || [],
    loading: SupportTicket?.ticketCommentsLoading || false,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getTicketCommentsRequest: (data) => dispatch(getTicketCommentsRequest(data)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(TicketCommentList);
